import { Checkbox, Input } from '@library/components/inputs';
import type {
  MissionConditionInputReferenceValue,
  MissionEventDefinitionProperty
} from '@library/types/missions';
import { useMemo, useState } from 'react';
import { PrizepicksDatePicker } from '@library/components/datePicker';
import { Select } from '@library/components/selects';
import type { SelectOption } from '@library/types/utils';
import { formatLabelByKey } from '@bo-utils/common';

export type MissionConditionInputSwitchProps = {
  referenceValue: MissionConditionInputReferenceValue;
  property: MissionEventDefinitionProperty;
  onChange: (value: MissionConditionInputReferenceValue) => void;
};

export const MissionConditionInputSwitch = ({
  referenceValue,
  property,
  onChange
}: MissionConditionInputSwitchProps) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const enumOptions = useMemo<SelectOption[]>(
    () =>
      property.enum_values &&
      property.enum_values.map((option) => ({
        key: option,
        name: formatLabelByKey(option),
        value: option
      })),
    [property.enum_values]
  );

  switch (property.property_type) {
    case 'boolean':
      return (
        <Checkbox
          id="referenceValueBoolean"
          label={formatLabelByKey(property.name)}
          labelClassName="text-xs uppercase"
          checked={!!referenceValue}
          onCheckboxChange={({ checked }) => onChange(checked)}
        />
      );
    case 'date':
    case 'date_with_time':
      return (
        <PrizepicksDatePicker
          handleChange={(date) => onChange(date)}
          open={datePickerOpen}
          setOpen={setDatePickerOpen}
          monthsShown={1}
          value={referenceValue as Date}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          className="bg-soFresh-110 text-soClean-100 border-soFresh-140 placeholder:text-soClean-170 block min-h-10 w-52 rounded-md border py-1.5 sm:text-sm sm:leading-6"
          value={referenceValue as number}
          onValueChange={onChange}
        />
      );
    case 'enum':
      return (
        <Select
          className="min-w-[200px]"
          optionsClassName="!t-6"
          label={formatLabelByKey(property.name)}
          onChange={(option) => onChange(option.value)}
          options={enumOptions}
          size="small"
          testId="enum-options"
          value={referenceValue as string}
          zIndex={40}
          portal
        />
      );
    case 'array':
    default:
      return (
        <Input
          className="bg-soFresh-110 text-soClean-100 border-soFresh-140 placeholder:text-soClean-170 block min-h-10 w-52 rounded-md border py-1.5 sm:text-sm sm:leading-6"
          value={referenceValue as string}
          onValueChange={onChange}
        />
      );
  }
};
